import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Building2,
  GraduationCap,
  Package,
  ArrowRight,
} from "lucide-react";

const childrenImg = "/assets/images/children.webp";

const services = [
  {
    id: 1,
    icon: BookOpen,
    title: "Basic Education Development",
    description:
      "Provides support for free, compulsory and quality basic education across public primary and junior secondary schools in Akwa Ibom State.",
  },
  {
    id: 2,
    icon: Building2,
    title: "School Infrastructure Provision",
    description:
      "Builds, renovates and equips classrooms, offices, libraries, laboratories and other learning facilities.",
  },
  {
    id: 3,
    icon: GraduationCap,
    title: "Teacher Development and Support",
    description:
      "Supports teacher training, capacity building, deployment and professional development to improve teaching quality.",
  },
  {
    id: 4,
    icon: Package,
    title: "Learning Materials and School Support",
    description:
      "Provides instructional materials, furniture and other resources needed to improve learning outcomes in public basic schools.",
  },
];

const Services = () => {
  const [highlightId, setHighlightId] = useState(2);

  return (
    <section className="relative py-20 overflow-hidden">
      {/* BG IMAGE */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${childrenImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      />

      {/* OVERLAY */}
      <div className="absolute inset-0 bg-white/85 backdrop-blur-[1px]" />

      {/* GRID PATTERN */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #06183f 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* GREEN SPLASH */}
      <div className="absolute -left-32 top-0 w-[420px] h-[420px] bg-[#06183f]/12 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* ORANGE SPLASH */}
      <div className="absolute -right-32 bottom-0 w-[420px] h-[420px] bg-[#ff6b00]/12 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* BLUE SPLASH */}
      <div className="absolute top-1/2 right-1/3 w-[350px] h-[350px] bg-[#06183f]/10 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* TOP LINE */}
      <div className="absolute top-0 left-0 w-full h-[5px] bg-gradient-to-r from-[#06183f] via-[#06183f] to-[#ff6b00]" />

      {/* CONTENT */}
      <div className="relative max-w-[1200px] mx-auto px-6 grid lg:grid-cols-[0.95fr_1.05fr] gap-10 items-start">
        {/* LEFT */}
        <div className="lg:sticky lg:top-24">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-[3px] bg-[#ff6b00]" />

            <span className="text-[#ff6b00] font-bold tracking-[0.2em] text-sm uppercase">
              Services
            </span>
          </div>

          <h2
            className="text-[42px] md:text-[56px] lg:text-[62px] font-black text-[#06183f] leading-[1.02] mb-7"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            AKSUBEB offers <br />
            these <span className="text-[#ff6b00]">key</span> <br />
            services.
          </h2>

          <p className="text-[#5b6478] leading-[1.9] text-[16px] mb-8 max-w-md">
            AKSUBEB supports public basic education across Akwa Ibom State
            through school development, infrastructure improvement, teacher
            support, and learning resource provision.
          </p>

          {/* BUTTON */}
          <Link
            to="/programs"
            className="group relative inline-flex items-center gap-3 pl-7 pr-12 py-4 bg-[#06183f] text-white font-bold hover:bg-[#ff6b00] transition-all duration-300 shadow-xl hover:scale-105"
            style={{
              clipPath:
                "polygon(0 0, 100% 0, 92% 50%, 100% 100%, 0 100%)",
            }}
          >
            Explore Services

            <ArrowRight
              size={18}
              className="absolute right-5 group-hover:translate-x-1 transition-transform"
            />
          </Link>
        </div>

        {/* RIGHT */}
        <div className="grid sm:grid-cols-2 gap-5">
          {services.map((s) => {
            const Icon = s.icon;

            const isHighlighted = s.id === highlightId;

            const cardClass = isHighlighted
              ? "bg-[#06183f] text-white shadow-2xl"
              : "bg-white/95 backdrop-blur-md text-[#06183f] shadow-sm hover:shadow-2xl hover:-translate-y-2 hover:bg-[#06183f] hover:text-white";

            return (
              <div
                key={s.id}
                onMouseEnter={() => setHighlightId(s.id)}
                className={`group relative p-7 pt-12 rounded-[28px] transition-all duration-300 cursor-pointer overflow-hidden border border-white/40 ${cardClass}`}
              >
                {/* TOP BAR */}
                <span
                  className={`absolute top-0 left-0 h-1.5 transition-all duration-500 ${
                    isHighlighted
                      ? "w-1/3 bg-[#ff6b00]"
                      : "w-28 bg-[#ff6b00] group-hover:w-full"
                  }`}
                />

                {/* BLUE SHAPE */}
                <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-[#06183f]/10 group-hover:bg-[#ff6b00]/10 transition-all duration-500" />

                {/* GREEN SHAPE */}
                <div className="absolute -left-20 -bottom-20 w-44 h-44 rounded-full bg-[#06183f]/8 group-hover:bg-[#06183f]/15 transition-all duration-500" />

                {/* ICON */}
                <div
                  className={`relative w-16 h-16 mb-6 flex items-center justify-center rounded-2xl transition-all duration-300 ${
                    isHighlighted
                      ? "bg-white/10 text-[#ff6b00]"
                      : "bg-[#06183f]/5 text-[#06183f] group-hover:bg-[#ff6b00] group-hover:text-white"
                  }`}
                >
                  <Icon size={32} strokeWidth={1.6} />
                </div>

                {/* TITLE */}
                <h3
                  className={`relative text-[20px] font-black mb-3 leading-tight transition-colors ${
                    isHighlighted
                      ? "text-white"
                      : "text-[#06183f] group-hover:text-white"
                  }`}
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  {s.title}
                </h3>

                {/* DESCRIPTION */}
                <p
                  className={`relative leading-relaxed text-[14.5px] transition-colors ${
                    isHighlighted
                      ? "text-white/80"
                      : "text-[#5b6478] group-hover:text-white/80"
                  }`}
                >
                  {s.description}
                </p>

                {/* LARGE BG ICON */}
                {isHighlighted && (
                  <Icon
                    size={160}
                    strokeWidth={0.6}
                    className="absolute -bottom-8 -right-6 text-white/5 pointer-events-none"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;